import * as React from "react";
import Link from "next/link";
import { IFtthOffer } from "@nju/data-access-portal-offers";
import { IFtthOrchestrator } from "@nju/data-access-portal-orchestrator";
import { useOrder } from "@nju/data-access-portal-order";
import { usePaymentsHistory } from "@nju/data-access-portal-payment";
import { useUser } from "@nju/data-access-portal-user";
import { useDecodedToken } from "@nju/data-access-portal-wwt";
import { pushSimpleEvent } from "@nju/data-layer";
import { OrderParameters } from "@nju/feature-portal-dashboard-components";
import { portalRoutes } from "@nju/routes";
import { A, Separator } from "@nju/ui";
import { Info } from "./partials/Info/Info";
import { LoginElement } from "./partials/Info/LoginElement";
import { OrderStatus } from "./partials/OrderStatus";
import { Payments } from "./partials/Payments";
import { SFHInfo } from "./partials/SFHInfo";
import { Title } from "./partials/Title";

interface IFtthStatusBannerDetailsProps {
  allowCancelOrder: boolean;
  activationFee: number;
  allowAppointmentDateChange: boolean;
  status: string;
  installationToken: string | null | undefined;
  sfh: boolean | undefined;
  note?: string;
  contactNumber?: string;
  appointmentTimeSlot: IFtthOrchestrator["appointmentTimeSlot"];
  offer: IFtthOffer;
  userId: string;
}

export function FtthStatusBannerDetails({
  allowCancelOrder,
  activationFee,
  installationToken,
  sfh,
  allowAppointmentDateChange,
  status,
  note,
  contactNumber,
  appointmentTimeSlot,
  offer,
  userId,
}: IFtthStatusBannerDetailsProps) {
  const { primaryPaymentMethod } = useUser();
  const order = useOrder();
  const decodedToken = useDecodedToken(installationToken);

  const {
    downloadFullText,
    uploadFullText,
    bundleDiscount,
    ftthBundleDiscount,
    shortFeatures,
  } = offer;

  const modem = shortFeatures.find((feature) => {
    return feature.name === "nju_ftth_short_modem";
  });

  const paymentsHistory = usePaymentsHistory();
  const isPending = !!paymentsHistory?.payments.find(
    ({ status, paymentMethod }) => {
      return status === "PENDING" && paymentMethod.methodType === "CREDIT_CARD";
    },
  );

  const isSfh = decodedToken?.address.isSfh || sfh;

  const handlePushCancelOrderDataLayer = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "service_resignation_complete_order",
    });
  }, []);

  const handleDataLayer = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "service_track",
    });
  }, []);

  return (
    <>
      <Separator size="s" tone="invisible" />
      {downloadFullText && <Title downloadSpeed={downloadFullText} />}
      <Separator size="m" tone="invisible" />
      {downloadFullText && uploadFullText && (
        <OrderParameters
          downloadSpeed={downloadFullText}
          modemName={modem?.description.pl}
          uploadSpeed={uploadFullText}
        />
      )}
      <Separator size="m" />
      <OrderStatus status={status} />
      <Separator size="m" />
      {order && order.additionalTask && order.internet && (
        <Payments
          activationFee={activationFee}
          bundleDiscount={ftthBundleDiscount || bundleDiscount}
          installationPrice={order.additionalTask.oneTimeItemPrice?.grossAmount}
          isPending={isPending}
          paymentMethod={primaryPaymentMethod}
          sfhPrice={order.sfh?.recurringItemPrice.grossAmount}
          subscriptionPrice={order.internet.recurringItemPrice.grossAmount}
          userId={userId}
        />
      )}
      <Separator size="m" />
      {order?.internet && (
        <>
          <Info
            allowAppointmentDateChange={allowAppointmentDateChange}
            appointmentTimeSlot={appointmentTimeSlot}
            contactNumber={contactNumber}
            decodedToken={decodedToken}
            note={note}
          />
          {isSfh && (
            <>
              <Separator size="m" />
              <SFHInfo
                installationStandard={
                  decodedToken?.address?.installationStandard
                }
                isSfh={decodedToken?.address?.isSfh}
              />
            </>
          )}
          {order.internet.product.login && (
            <>
              <Separator size="s" />
              <LoginElement login={order.internet.product.login} />
            </>
          )}
        </>
      )}
      {order?.utechUserUrl && (
        <>
          <Separator size="s" tone="invisible" />
          <Link href={order.utechUserUrl} passHref={true} target="_blank">
            <A
              display={{
                "@onlyMobile": "flex",
                "@fromTablet": "inlineBlock",
              }}
              onClick={handleDataLayer}
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
              variant="secondary"
            >
              śledź zamówienie
            </A>
          </Link>
        </>
      )}
      {allowCancelOrder && (
        <>
          <Separator size="s" tone="invisible" />
          <Link
            href={portalRoutes.mojeKonto.anulujZamowienie.get({
              mode: "ftth",
            })}
            passHref={true}
          >
            <A
              display={{
                "@onlyMobile": "flex",
                "@fromTablet": "inlineBlock",
              }}
              onClick={handlePushCancelOrderDataLayer}
              size={{
                "@onlyMobile": "s",
                "@fromTablet": "m",
              }}
              variant="secondary"
            >
              anuluj zamówienie
            </A>
          </Link>
        </>
      )}
    </>
  );
}
